import { Component, OnInit } from '@angular/core';
import { FileUploadService } from 'src/app/services/file-upload/file-upload.service';
import { Observable, from, combineLatest } from 'rxjs';
import { take, startWith, shareReplay, map } from 'rxjs/operators';
import { ImageContent } from 'src/app/models/data/image-content.model';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { DropdownService } from 'src/app/services/dropdown/dropdown.service';
import { CompletionQuestionsService } from 'src/app/services/completion-questions/completion-questions.service';

@Component({
  selector: 'app-file-upload-detail',
  templateUrl: './file-upload-detail.component.html',
  styleUrls: ['./file-upload-detail.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    NgxPaginationModule,
  ],
})
export class FileUploadDetailComponent implements OnInit {
  images$!: Observable<ImageContent[]>;
  isUploading$!: Observable<boolean>;
  buttonState$!: Observable<{ images: ImageContent[], isUploading: boolean }>;
  headers = ['Thumbnail', 'Title', 'Safe for the web (yes/no)?', 'Actions'];
  p: number = 1;
  editState = false;
  itemToEdit!: ImageContent;
  form!: FormGroup;
  submitted = false;
  notApplicable: boolean = false;
  isNavigating = false;

  constructor(
    private fileService: FileUploadService,
    private router: Router,
    private dropdownService: DropdownService,
    private completionQuestionsService: CompletionQuestionsService,
  ) { }

  ngOnInit(): void {
    this.form = new FormGroup({
      Title: new FormControl(null, [Validators.required]),
      IsSafe: new FormControl(null, [Validators.required]),
    });
    this.getData();
  }

  onDelete(image: ImageContent) {
    if (this.editState == true) {
      return;
    }
    if (confirm('Are you sure?')) {
      this.fileService.deleteImage(image);
      this.getData();
      // Check if all photos are deleted and checkbox is unchecked - if so, mark completion question as incomplete
      setTimeout(() => {
        this.images$.pipe(take(1)).subscribe(images => {
          const imagesCountAfter = images?.length || 0;
          
          // If no photos remain and checkbox is unchecked, mark completion question as incomplete
          if (imagesCountAfter === 0 && !this.notApplicable) {
            this.completionQuestionsService.updateCompletionQuestion(45, false, false).subscribe({
              next: () => {
                // Refresh completion status from API after updating completion question
                this.dropdownService.setPageCompletionStatuses();
              },
              error: (error) => {
                console.error('Error marking completion question as incomplete:', error);
                // Still refresh status even if update fails
                this.dropdownService.setPageCompletionStatuses();
              }
            });
          } else {
            // Photos still exist or checkbox is checked - just refresh status
            this.dropdownService.setPageCompletionStatuses();
          }
        });
      }, 100);
    }
  }

  getData() {
    this.fileService.getImages().subscribe();
    this.images$ = this.fileService.getImagesObs().pipe(
      startWith([]),
      shareReplay(1)
    );
    this.isUploading$ = this.fileService.isUploading$.pipe(
      startWith(false),
      shareReplay(1)
    );
    // Combine both observables so button is always visible
    this.buttonState$ = combineLatest([
      this.images$,
      this.isUploading$
    ]).pipe(
      map(([images, isUploading]) => ({ images, isUploading })),
      startWith({ images: [], isUploading: false }),
      shareReplay(1)
    );
    console.log(this.images$);
    // Initialize checkbox state
    this.initializeNotApplicableState();
  }

  initializeNotApplicableState() {
    this.completionQuestionsService.getQuestion(45).subscribe({
      next: (question) => {
        if (question) {
          console.log(`Loading notApplicable state for question 45:`, question.notApplicable);
          // Always update from backend - it's the source of truth
          if (question.notApplicable === true) {
            this.notApplicable = true;
            console.log(`Checkbox set to checked for question 45`);
          } else {
            // Set to false if null, false, or undefined
            this.notApplicable = false;
            console.log(`Checkbox set to unchecked for question 45`);
          }
        } else {
          console.warn(`Question 45 not found, preserving current checkbox state:`, this.notApplicable);
        }
      },
      error: (error) => {
        console.warn(`Failed to load notApplicable state for question 45, preserving current state:`, this.notApplicable, error);
      }
    });
  }

  onNotApplicableChange() {
    this.completionQuestionsService.updateNotApplicable(45, this.notApplicable)
      .subscribe({
        next: (result) => {
          console.log(`NotApplicable updated to ${this.notApplicable} for question 45`, result);
          // Refresh page completion statuses to update dropdown
          // Add a small delay to ensure backend has processed the update
          setTimeout(() => {
            this.dropdownService.setPageCompletionStatuses();
          }, 300);
        },
        error: (error) => {
          console.error(`Failed to update NotApplicable for question 45:`, error);
          // If update fails, revert the checkbox state
          this.notApplicable = !this.notApplicable;
        }
      });
  }

  onEdit(image: ImageContent) {
    if (this.editState == true) {
      return;
    }
    this.itemToEdit = image;
    this.editState = !this.editState;

    // Set form values when editing
    this.form.patchValue({
      Title: image.Title,
      IsSafe: image.IsSafe
    });
  }

  onCancel() {
    this.editState = !this.editState;
    this.getData();
  }

  onSubmit() {
    this.editState = false;
    console.log(this.form.value);
    this.fileService
      .editImage(this.form.value, this.itemToEdit.ImageID)
      .subscribe((res) => {
        console.log(res);
        // Refresh completion status from API
        this.dropdownService.setPageCompletionStatuses();
      });
  }

  onSurveySubmit() {
    // Guard against multiple simultaneous navigation attempts
    if (this.isNavigating) {
      console.log('Navigation already in progress, ignoring click');
      return;
    }
    
    console.log('onSurveySubmit() called, notApplicable:', this.notApplicable);
    
    // Get images count immediately - the observable now uses startWith([]) and shareReplay(1)
    // so it will always emit immediately with at least an empty array
    this.images$.pipe(take(1)).subscribe({
      next: (images) => {
        const imagesCount = images?.length || 0;
        console.log('Images received in onSurveySubmit:', images, 'length:', imagesCount);
        this.performNavigation(imagesCount);
      },
      error: (error) => {
        console.error('Error getting images:', error);
        // If we can't get images but checkbox is checked, proceed anyway
        if (this.notApplicable) {
          console.log('Error getting images, but notApplicable is checked, proceeding with navigation');
          this.performNavigation(0);
        }
      }
    });
  }

  private performNavigation(imagesCount: number) {
    // Secondary guard check (in case method is called directly)
    if (this.isNavigating) {
      return;
    }
    
    this.isNavigating = true;
    
    if (imagesCount > 0) {
      // Existing logic: mark completion and navigate
      console.log('Navigating with photos');
      // Preserve notApplicable state if it was set (though checkbox shouldn't be visible with photos)
      this.completionQuestionsService.updateCompletionQuestion(45, true, this.notApplicable).subscribe({
        next: () => {
          console.log('Completion question updated, navigating to submit-survey');
          this.router.navigate(['submit-survey']).finally(() => {
            this.isNavigating = false;
          });
        },
        error: (error) => {
          this.isNavigating = false;
          console.error('Error updating completion question:', error);
        }
      });
    } else {
      // New logic: require checkbox to be checked
      if (!this.notApplicable) {
        // Show validation message or prevent navigation
        console.log('Cannot navigate: No photos and checkbox not checked');
        this.isNavigating = false;
        return;
      }
      // Mark completion question and navigate
      // Explicitly pass notApplicable: true to ensure it's persisted
      console.log('Navigating with notApplicable checked');
      this.completionQuestionsService.updateCompletionQuestion(45, true, true).subscribe({
        next: () => {
          console.log('Completion question updated, navigating to submit-survey');
          this.router.navigate(['submit-survey']).finally(() => {
            this.isNavigating = false;
          });
        },
        error: (error) => {
          this.isNavigating = false;
          console.error('Error updating completion question:', error);
        }
      });
    }
  }
}
